import React, { useState, useEffect } from "react";
import styled from "styled-components";

import { arrayOf, bool, oneOfType, string, array } from "prop-types";
import { AnimatePresence, motion } from "framer-motion";
import { useMedia } from "react-use";
import { useTranslation } from "@lib/i18n";
import { useDispatch } from "react-redux";
import { useRouter } from "next/router";

import FilterResultItem from "@components/TestDrive/FilterResultItem";

import gtm from "@gtm/core";
import Typography from "@components/shared/Typography";
import ArrowRightIcon from "@components/shared/Icons/ArrowRight";
import ArrowLeftIcon from "@components/shared/Icons/ArrowLeft";
import { toast } from "@lib/reactToastify";

import { color, desktop, fontSize, fontWeight } from "@components/shared/utils";
import { testDriveLocationType, testDriveVehicleType } from "@types";
import { useIsUserTrustedSelector } from "@redux/reducers/auth";
import { ROUTES } from "@shared/routing/routes";
import { testDriveMoreResultsEvent } from "@gtm/events/testdrive";
import { useRoutingUtils } from "@hooks/useRoutingUtils";

import {
  TEST_DRIVE_TYPE_ID,
  CTA_TD_TYPES,
  DEAL_TYPE,
  LP_DEMOOZ_URL,
} from "@shared/constants";

import {
  setTestDrivePointAction,
  setTestDriveSlotAction,
} from "@redux/actions/testDriveActions";

import {
  createNewDealFromExistingAction,
  postDealAction,
} from "@redux/actions/dealActions";

import {
  useCurrentDealSelector,
  useLastCtaSelector,
} from "@redux/reducers/deal";

const Container = styled.div`
  padding: 0 2rem;
  background: ${color("white")};

  ${desktop`
    min-height: 415px;
    padding-bottom: 50px;
  `}
`;

const Title = styled(Typography)`
  font-size: ${fontSize("md")};
  font-weight: ${fontWeight("bold")};
  text-transform: uppercase;
  padding: 1rem 0;
`;

const ShowRemaining = styled(Typography)`
  padding: 2rem 0;
  font-size: 1.25rem;
  cursor: pointer;
  display: flex;
`;

const CustomIcon = `
  width: 1rem;
  height: 1rem;
  margin-left: 1rem;
`;

const CustomArrowRightIcon = styled(ArrowRightIcon)`
  ${CustomIcon}
`;

const CustomArrowLeftIcon = styled(ArrowLeftIcon)`
  ${CustomIcon}
`;

const FilterResultList = ({
  testDrivePoints = [],
  isOpen,
  activePointId,
  accentColor,
}) => {
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const routingUtils = useRoutingUtils();
  const dispatch = useDispatch();
  const isUserTrusted = useIsUserTrustedSelector();
  const currentDeal = useCurrentDealSelector();
  const lastCta = useLastCtaSelector();

  const [isOpenRemainingSection, setIsOpenRemainingSection] = useState(false);
  const nearest = testDrivePoints.slice(0, 4);
  const remaining = testDrivePoints.slice(4, 10);
  const isDesktopView = useMedia(`(min-width: 1024px)`);

  const isTestDriveCompleted =
    currentDeal?.dealType === DEAL_TYPE.TEST_DRIVE &&
    lastCta === CTA_TD_TYPES.RESERVATION_COMPLETED;

  const handleItemPick = async point => {
    dispatch(setTestDrivePointAction(point));
    dispatch(setTestDriveSlotAction(null));

    // Create new deal only if existing deal is not TD or finished TD
    // otherwise just update deal
    if (
      isUserTrusted &&
      (currentDeal?.dealType !== DEAL_TYPE.TEST_DRIVE ||
        isTestDriveCompleted ||
        point.type === TEST_DRIVE_TYPE_ID.FNAC_DARTY)
    ) {
      await dispatch(createNewDealFromExistingAction());
    }

    return dispatch(
      postDealAction(CTA_TD_TYPES.LOCATION_SELECTED, DEAL_TYPE.TEST_DRIVE)
    );
  };

  const getTargetRouteObject = point => {
    switch (point.type) {
      case TEST_DRIVE_TYPE_ID.STORE:
        return {
          route: ROUTES.TEST_DRIVE_DETAIL_ENSTORE,
          asPath: routingUtils.getLocalizedPathname(
            ROUTES.TEST_DRIVE_DETAIL_ENSTORE,
            { testDrivePointSlug: point.slug },
            i18n.language
          ),
        };

      case TEST_DRIVE_TYPE_ID.FNAC_DARTY:
        return {
          route: ROUTES.TEST_DRIVE_DETAIL_FNAC_DARTY,
          asPath: routingUtils.getLocalizedPathname(
            ROUTES.TEST_DRIVE_DETAIL_FNAC_DARTY,
            { testDrivePointSlug: point.slug },
            i18n.language
          ),
        };

      case TEST_DRIVE_TYPE_ID.HOME:
        return {
          route: ROUTES.TEST_DRIVE_HOME,
          asPath: routingUtils.getLocalizedPathname(
            ROUTES.TEST_DRIVE_HOME,
            null,
            i18n.language
          ),
        };

      case TEST_DRIVE_TYPE_ID.MEDIAMARKT:
        return {
          route: ROUTES.TEST_DRIVE_DETAIL_MEDIAMARKT,
          asPath: routingUtils.getLocalizedPathname(
            ROUTES.TEST_DRIVE_DETAIL_MEDIAMARKT,
            { testDrivePointSlug: point.slug },
            i18n.language
          ),
        };

      case TEST_DRIVE_TYPE_ID.SUPER:
        return {
          route: LP_DEMOOZ_URL + t("testDrive.demoozAmbassadorPath"),
          asPath: LP_DEMOOZ_URL + t("testDrive.demoozAmbassadorPath"),
        };

      case TEST_DRIVE_TYPE_ID.LAMAISON:
      default:
        return {
          route: ROUTES.TEST_DRIVE_DETAIL_LA_MAISON,
          asPath: routingUtils.getLocalizedPathname(
            ROUTES.TEST_DRIVE_DETAIL_LA_MAISON,
            { testDrivePointSlug: point.slug },
            i18n.language
          ),
        };
    }
  };

  const onRequestSuccess = async point => {
    const { route, asPath } = getTargetRouteObject(point);

    await router.push(route, asPath);

    return new Promise(() => null);
  };

  const toggleOpen = () => {
    setIsOpenRemainingSection(!isOpenRemainingSection);
    gtm.fire(testDriveMoreResultsEvent());
  };

  useEffect(() => {
    if (!isOpenRemainingSection && isDesktopView) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [isOpenRemainingSection]);

  const onFilterResultItemError = error => {
    toast.error(t("errors.default"), {}, error);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Container>
      <Title>{t("testDrive.filterResults")}</Title>
      {nearest.map(point => (
        <FilterResultItem
          key={point.id}
          point={point}
          activePointId={activePointId}
          accentColor={accentColor}
          triggerRequestCallback={() => handleItemPick(point)}
          onRequestSuccess={() => onRequestSuccess(point)}
          onRequestError={onFilterResultItemError}
        />
      ))}
      <AnimatePresence>
        {isOpenRemainingSection && (
          <motion.div
            initial={{
              height: 0,
              display: "none",
            }}
            animate={{
              height: "auto",
              display: "block",
            }}
            exit={{
              height: 0,
              transitionEnd: { display: "none" },
            }}
            style={{ overflow: "hidden" }}
            transition={{ duration: 0.6 }}
          >
            {remaining.map(point => (
              <FilterResultItem
                key={point.id}
                point={point}
                activePointId={activePointId}
                accentColor={accentColor}
                triggerRequestCallback={() => handleItemPick(point)}
                onRequestSuccess={() => onRequestSuccess(point)}
                onRequestError={onFilterResultItemError}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
      {remaining.length > 0 && (
        <ShowRemaining bold uppercase onClick={toggleOpen}>
          {isOpenRemainingSection ? (
            <>
              {t("testDrive.showLess")} <CustomArrowLeftIcon />
            </>
          ) : (
            <>
              {t("testDrive.showRemaining")}
              <CustomArrowRightIcon />
            </>
          )}
        </ShowRemaining>
      )}
    </Container>
  );
};

FilterResultList.propTypes = {
  isOpen: bool.isRequired,
  accentColor: string.isRequired,
  testDriveTypes: array,
  affiliation: string,
  activePointId: string,
  testDrivePoints: arrayOf(
    oneOfType([testDriveLocationType, testDriveVehicleType])
  ),
};

export default FilterResultList;
